import { bytes as formatBytes } from '@/components/format';

// ─── Wofür der Platz draufgeht ───────────────────────────────────────────────
//
// Ein liegender gestapelter Balken: die Frage ist „welcher Anteil vom Ganzen",
// und dafür ist ein Balken lesbarer als ein Kreis – Längen lassen sich
// vergleichen, Kreissegmente nicht.
//
// Die sechs Farben sind nicht gegriffen, sondern geprüft: Helligkeitsband,
// Sättigung, Kontrast zur Fläche und der Abstand benachbarter Paare bei
// Farbfehlsichtigkeit (schlechtestes Paar ΔE 16,7 – die Grenze liegt bei 8).
// Die Reihenfolge ist Teil des Ergebnisses: Bernstein und Grün standen zuerst
// nebeneinander und lagen bei ΔE 7,9, also knapp über dem Boden.
//
// Zusätzlich trägt jede Farbe ihren Namen in der Legende und die Segmente
// haben eine Lücke – Farbe allein soll nie die einzige Auskunft sein.

const FARBEN = ['#2563eb', '#059669', '#7c3aed', '#d97706', '#0891b2', '#e11d48'];
/** Alles jenseits der sechs Farben wird zusammengefasst statt eingefärbt. */
const REST = '#94a3b8';

const KATEGORIE_LABELS: Record<string, string> = {
  photo: 'Fotos',
  document: 'Dokumente',
  certificate: 'Urkunden',
  letter: 'Briefe',
  artifact: 'Artefakte',
  map: 'Karten',
  newspaper: 'Zeitungen',
  churchbook: 'Kirchenbücher',
  audio: 'Audio',
  video: 'Video',
  other: 'Sonstiges',
};

export function kategorieName(k: string): string {
  return KATEGORIE_LABELS[k] ?? k;
}

interface Teil {
  kategorie: string;
  bytes: number;
  dateien: number;
}

interface Props {
  teile: Teil[];
  /**
   * Platz, der auf der Platte liegt, aber zu keiner Kategorie gehört – die
   * verwaisten Dateien.
   *
   * Bekommt ein eigenes Segment statt eine Lücke am Balkenende. Die erste
   * Fassung setzte hier nur die Gesamtgröße ein; der Balken endete dann bei
   * der Hälfte und sah aus, als fehle etwas. Er zeigt jetzt, WAS fehlt.
   */
  nichtZugeordnet?: number;
}

export function SpeicherDiagramm({ teile, nichtZugeordnet = 0 }: Props) {
  const summe = teile.reduce((s, t) => s + t.bytes, 0) + Math.max(0, nichtZugeordnet);

  if (summe === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
        Noch nichts abgelegt.
      </p>
    );
  }

  // Mehr als sechs Kategorien werden nicht bunter, sondern zusammengefasst.
  // Eine siebte Farbe wäre eine, die niemand mehr von den anderen trennt.
  const sortiert = [...teile].sort((a, b) => b.bytes - a.bytes);
  const sichtbar = sortiert.slice(0, FARBEN.length);
  const rest = sortiert.slice(FARBEN.length);
  const restBytes = rest.reduce((s, t) => s + t.bytes, 0);

  const eintraege = [
    ...sichtbar.map((t, i) => ({ ...t, farbe: FARBEN[i]!, name: kategorieName(t.kategorie) })),
    ...(restBytes > 0
      ? [
          {
            kategorie: 'rest',
            bytes: restBytes,
            dateien: rest.reduce((s, t) => s + t.dateien, 0),
            farbe: REST,
            name: `${rest.length} weitere`,
          },
        ]
      : []),
    ...(nichtZugeordnet > 0
      ? [
          {
            kategorie: 'verwaist',
            bytes: nichtZugeordnet,
            dateien: 0,
            farbe: REST,
            name: 'nicht zugeordnet',
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* Der Balken. `gap` statt Rahmen: eine Lücke trennt auch dann, wenn
          zwei Farben nicht unterscheidbar sind. */}
      <div className="flex h-6 gap-[2px] overflow-hidden rounded-md" role="img"
        aria-label={`Belegung nach Kategorie: ${eintraege
          .map((e) => `${e.name} ${formatBytes(e.bytes)}`)
          .join(', ')}`}
      >
        {eintraege.map((e) => (
          <div
            key={e.kategorie}
            className="h-full first:rounded-l-md last:rounded-r-md"
            style={{ width: `${(e.bytes / summe) * 100}%`, backgroundColor: e.farbe }}
            title={`${e.name}: ${formatBytes(e.bytes)}`}
          />
        ))}
      </div>

      {/* Die Legende trägt die Zahlen mit. Ohne sie wäre der Balken hübsch und
          nutzlos: „ungefähr ein Drittel" beantwortet keine Frage. */}
      <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {eintraege.map((e) => (
          <li key={e.kategorie} className="flex items-center gap-2 text-sm">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: e.farbe }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-slate-600">{e.name}</span>
            <span className="shrink-0 tabular-nums text-slate-500">{formatBytes(e.bytes)}</span>
            <span className="w-10 shrink-0 text-right tabular-nums text-xs text-slate-400">
              {Math.round((e.bytes / summe) * 100)} %
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Auslastungsbalken ───────────────────────────────────────────────────────
//
// Kein kategoriales Schema, sondern ein Zustand: unauffällig, wird knapp,
// ist voll. Deshalb Statusfarben – und dazu immer die Zahl, weil ein rotes
// Balkenende ohne Prozentangabe nur Beunruhigung ist.

export function Auslastung({ prozent }: { prozent: number | null }) {
  if (prozent === null) {
    return <span className="text-xs text-slate-400">unbegrenzt</span>;
  }

  const voll = prozent >= 100;
  const knapp = prozent >= 80;
  const farbe = voll ? 'bg-red-500' : knapp ? 'bg-amber-500' : 'bg-blue-500';

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${farbe}`} style={{ width: `${Math.min(100, prozent)}%` }} />
      </div>
      <span
        className={`shrink-0 tabular-nums text-xs ${
          voll ? 'font-medium text-red-600' : knapp ? 'font-medium text-amber-700' : 'text-slate-500'
        }`}
      >
        {prozent} %
      </span>
    </div>
  );
}
